// ─── 기업 유형 타입 ────────────────────────────────────────────────
export type CompanyType =
  | 'MusicLabel'
  | 'LiveEntertainment'
  | 'CulturalFoundation'
  | 'WebtoonStudio'
  | 'ContentAgency';

// ─── 직무 타입 (전체) ──────────────────────────────────────────────
export type JobRole =
  // 공통 / MusicLabel
  | 'CEO' | 'AR' | 'Finance' | 'Marketing' | 'Legal'
  // LiveEntertainment
  | 'PD' | 'Ticketing'
  // CulturalFoundation
  | 'Projects' | 'Fundraising' | 'Evaluation'
  // WebtoonStudio
  | 'Editor' | 'Contract'
  // ContentAgency
  | 'Creative' | 'AE' | 'Production' | 'Data';

// ─── 네비게이션 아이템 전체 맵 ─────────────────────────────────────
export const ALL_NAV_ITEMS: Record<string, { path: string; icon: string; label: string }> = {
  dashboard:       { path: '/dashboard',       icon: 'ri-dashboard-3-line',     label: '대시보드' },
  inbox:           { path: '/inbox',           icon: 'ri-mail-line',            label: '인박스' },
  approval:        { path: '/approval',        icon: 'ri-file-list-3-line',     label: '전자결재' },
  'ar-hub':        { path: '/ar-hub',          icon: 'ri-music-2-line',         label: 'A&R 허브' },
  finance:         { path: '/finance',         icon: 'ri-bank-line',            label: '재무 / ERP' },
  marketing:       { path: '/marketing',       icon: 'ri-megaphone-line',       label: '마케팅 워룸' },
  legal:           { path: '/legal',           icon: 'ri-scales-3-line',        label: '법무 & IP' },
  'concert-hub':   { path: '/concert-hub',     icon: 'ri-mic-line',             label: '공연기획' },
  'foundation-hub':{ path: '/foundation-hub',  icon: 'ri-community-line',       label: '사업관리' },
  'ip-hub':        { path: '/ip-hub',          icon: 'ri-copyright-line',       label: 'IP 스튜디오' },
  'campaign-hub':  { path: '/campaign-hub',    icon: 'ri-advertisement-line',   label: '캠페인 관리' },
};

// ─── RoleConfig 타입 ──────────────────────────────────────────────
export type RoleConfig = {
  key: JobRole;
  label: string;
  labelEn: string;
  icon: string;
  description: string;
  tasks: string[];
  mainNav: string[];
};

// ─── 기업 유형별 직무 configs ──────────────────────────────────────
export const COMPANY_ROLE_CONFIGS: Record<CompanyType, RoleConfig[]> = {
  MusicLabel: [
    {
      key: 'CEO',
      label: 'CEO / 대표이사',
      labelEn: 'Chief Executive Officer',
      icon: 'ri-vip-crown-line',
      description: '회사 전체를 총괄합니다. 모든 부서의 현황을 파악하고 최종 결재권을 행사합니다.',
      tasks: ['전사 현황 모니터링', '최종 계약 결재', '부서 간 조율', '투자자 미팅'],
      mainNav: ['dashboard', 'inbox', 'approval', 'ar-hub', 'finance', 'marketing', 'legal'],
    },
    {
      key: 'AR',
      label: 'A&R 담당',
      labelEn: 'Artists & Repertoire',
      icon: 'ri-music-2-line',
      description: '아티스트를 발굴하고 앨범 콘셉트를 기획합니다. 데모 수급과 기획안 평가를 담당합니다.',
      tasks: ['데모곡 수급 및 평가', '앨범 기획서 작성', '무드보드 구성', '아티스트 계약 협상'],
      mainNav: ['dashboard', 'inbox', 'ar-hub', 'approval'],
    },
    {
      key: 'Finance',
      label: '재무 담당',
      labelEn: 'Finance & Accounting',
      icon: 'ri-bank-line',
      description: '회사의 자금을 관리합니다. 예산안을 심사하고 세금 납부와 급여 정산을 처리합니다.',
      tasks: ['예산안 심사 및 승인', '가상 계좌 관리', '세금 납부 처리', '월별 손익 보고'],
      mainNav: ['dashboard', 'inbox', 'finance', 'approval'],
    },
    {
      key: 'Marketing',
      label: '마케팅 담당',
      labelEn: 'Marketing & Fan Management',
      icon: 'ri-megaphone-line',
      description: '앨범을 홍보하고 여론을 관리합니다. SNS 운영, 보도자료 배포, 음원 차트를 담당합니다.',
      tasks: ['SNS 콘텐츠 기획', '보도자료 작성 및 배포', '음원 차트 분석', '위기 대응 입장문'],
      mainNav: ['dashboard', 'inbox', 'marketing'],
    },
    {
      key: 'Legal',
      label: '법무 담당',
      labelEn: 'Legal & IP Management',
      icon: 'ri-scales-3-line',
      description: '계약서의 독소 조항을 방어하고 저작권을 관리합니다.',
      tasks: ['계약서 독소 조항 검토', '저작권 등록', '내용증명 대응', '표준 계약서 관리'],
      mainNav: ['dashboard', 'inbox', 'legal', 'approval'],
    },
  ],

  LiveEntertainment: [
    {
      key: 'CEO',
      label: '대표이사',
      labelEn: 'Chief Executive Officer',
      icon: 'ri-vip-crown-line',
      description: '공연기획사 전체를 총괄합니다. 라인업 최종 결정과 스폰서 계약 결재권을 행사합니다.',
      tasks: ['아티스트 라인업 최종 확정', '주요 스폰서 계약 결재', '공연 손익 검토', '외부 파트너 미팅'],
      mainNav: ['dashboard', 'inbox', 'concert-hub', 'approval', 'finance', 'marketing'],
    },
    {
      key: 'PD',
      label: '공연기획 PD',
      labelEn: 'Production Director',
      icon: 'ri-mic-line',
      description: '공연의 기획부터 무대 구성까지 총괄합니다. 아티스트 섭외와 세트리스트를 관리합니다.',
      tasks: ['공연 기획안 작성', '아티스트 섭외 협상', '세트리스트 구성', '리허설 일정 관리'],
      mainNav: ['dashboard', 'inbox', 'concert-hub', 'approval'],
    },
    {
      key: 'Ticketing',
      label: '티켓영업 MD',
      labelEn: 'Ticketing & Sales',
      icon: 'ri-ticket-line',
      description: '티켓 판매 전략을 수립하고 판매 플랫폼을 관리합니다. 객석 점유율을 극대화합니다.',
      tasks: ['티켓 가격 전략 수립', '판매 채널 관리', '얼리버드 프로모션', '단체 구매 협상'],
      mainNav: ['dashboard', 'inbox', 'concert-hub'],
    },
    {
      key: 'Marketing',
      label: '마케팅팀장',
      labelEn: 'Marketing Manager',
      icon: 'ri-megaphone-line',
      description: '공연 홍보와 아티스트 SNS 캠페인을 총괄합니다.',
      tasks: ['공연 홍보물 기획', 'SNS 캠페인 운영', '보도자료 배포', '바이럴 마케팅'],
      mainNav: ['dashboard', 'inbox', 'marketing'],
    },
    {
      key: 'Finance',
      label: '재무담당',
      labelEn: 'Finance Manager',
      icon: 'ri-bank-line',
      description: '공연 예산 집행과 스폰서 정산을 담당합니다.',
      tasks: ['공연 예산 관리', '스폰서 수익 정산', '수익 배분 계산', '세금 신고'],
      mainNav: ['dashboard', 'inbox', 'finance', 'approval'],
    },
  ],

  CulturalFoundation: [
    {
      key: 'CEO',
      label: '사무국장',
      labelEn: 'Secretary General',
      icon: 'ri-vip-crown-line',
      description: '재단 전체 운영을 총괄합니다. 이사회에 사업 결과를 보고하고 보조금 수주를 결정합니다.',
      tasks: ['이사회 보고', '보조금 신청 결재', '사업 방향 결정', '외부 기관 협력'],
      mainNav: ['dashboard', 'inbox', 'foundation-hub', 'approval', 'finance'],
    },
    {
      key: 'Projects',
      label: '사업팀장',
      labelEn: 'Project Manager',
      icon: 'ri-community-line',
      description: '문화예술 지원 사업을 기획하고 집행합니다. 수혜자 선정과 사업 모니터링을 담당합니다.',
      tasks: ['사업 기획서 작성', '수혜자 선정 심사', '사업 실적 관리', '결과 보고서'],
      mainNav: ['dashboard', 'inbox', 'foundation-hub', 'approval'],
    },
    {
      key: 'Fundraising',
      label: '후원개발팀',
      labelEn: 'Fundraising Manager',
      icon: 'ri-heart-line',
      description: '기업 후원과 개인 기부금을 유치합니다. 후원자 CRM과 감사 행사를 담당합니다.',
      tasks: ['기업 후원 제안서', '후원자 CRM 관리', '기부금 캠페인', '감사 행사 기획'],
      mainNav: ['dashboard', 'inbox', 'foundation-hub'],
    },
    {
      key: 'Finance',
      label: '회계담당',
      labelEn: 'Nonprofit Accountant',
      icon: 'ri-bank-line',
      description: '비영리 회계 기준으로 보조금 수입·지출을 관리합니다. 세무신고와 감사 자료를 준비합니다.',
      tasks: ['보조금 수입·지출 관리', '비영리 전표 처리', '외부 감사 대응', '세무 신고'],
      mainNav: ['dashboard', 'inbox', 'finance', 'approval'],
    },
    {
      key: 'Evaluation',
      label: '사업평가위원',
      labelEn: 'Program Evaluator',
      icon: 'ri-star-line',
      description: '지원 사업의 효과를 평가하고 개선 방향을 제시합니다.',
      tasks: ['사업 성과 지표 설계', '수혜자 인터뷰', '임팩트 보고서', '다음 연도 예산 제언'],
      mainNav: ['dashboard', 'inbox', 'foundation-hub'],
    },
  ],

  WebtoonStudio: [
    {
      key: 'CEO',
      label: '대표이사',
      labelEn: 'Chief Executive Officer',
      icon: 'ri-vip-crown-line',
      description: '스튜디오 전체를 총괄합니다. 플랫폼 계약과 IP 전략을 최종 결정합니다.',
      tasks: ['플랫폼 계약 결재', 'IP 전략 결정', '해외 판권 협상', '투자 유치 미팅'],
      mainNav: ['dashboard', 'inbox', 'ip-hub', 'approval', 'legal', 'marketing'],
    },
    {
      key: 'Editor',
      label: '에디터',
      labelEn: 'Webtoon Editor',
      icon: 'ri-quill-pen-line',
      description: '작가를 발굴하고 연재 기획을 담당합니다. 원고 피드백과 스케줄 관리가 핵심입니다.',
      tasks: ['작가 발굴 및 계약', '연재 스케줄 관리', '원고 편집 피드백', '플랫폼 업로드'],
      mainNav: ['dashboard', 'inbox', 'ip-hub'],
    },
    {
      key: 'Contract',
      label: '계약 MD',
      labelEn: 'IP Licensing Manager',
      icon: 'ri-file-paper-2-line',
      description: 'IP 라이선싱과 2차 저작물 계약을 담당합니다. 드라마·게임·굿즈 파트너를 유치합니다.',
      tasks: ['IP 라이선싱 계약', '드라마 판권 협상', '굿즈 파트너 계약', '해외 수출 MOU'],
      mainNav: ['dashboard', 'inbox', 'ip-hub', 'approval'],
    },
    {
      key: 'Marketing',
      label: '마케터',
      labelEn: 'IP Marketing',
      icon: 'ri-megaphone-line',
      description: '웹툰 작품을 플랫폼에서 바이럴 확산시킵니다. 팬덤 형성과 미디어 믹스를 담당합니다.',
      tasks: ['플랫폼 추천 노출 협상', 'SNS 팬덤 운영', '미디어믹스 홍보', 'IP 브랜딩'],
      mainNav: ['dashboard', 'inbox', 'marketing'],
    },
    {
      key: 'Legal',
      label: '법무담당',
      labelEn: 'IP Legal Counsel',
      icon: 'ri-scales-3-line',
      description: '저작권을 보호하고 표절 분쟁에 대응합니다. 모든 계약서 독소 조항을 검토합니다.',
      tasks: ['저작권 등록', '표절 시비 대응', '플랫폼 계약 검토', '작가 전속 계약'],
      mainNav: ['dashboard', 'inbox', 'legal', 'approval'],
    },
  ],

  ContentAgency: [
    {
      key: 'CEO',
      label: '대표이사',
      labelEn: 'Chief Executive Officer',
      icon: 'ri-vip-crown-line',
      description: '에이전시 전체를 총괄합니다. 주요 클라이언트 수주와 사업 방향을 결정합니다.',
      tasks: ['주요 캠페인 수주 결재', '클라이언트 임원 미팅', '에이전시 전략 수립', 'P&L 검토'],
      mainNav: ['dashboard', 'inbox', 'campaign-hub', 'approval', 'finance', 'marketing'],
    },
    {
      key: 'Creative',
      label: '크리에이티브 디렉터',
      labelEn: 'Creative Director',
      icon: 'ri-paint-brush-line',
      description: '캠페인의 크리에이티브 방향성을 결정합니다. 브랜드 아이덴티티와 비주얼을 총괄합니다.',
      tasks: ['크리에이티브 브리프 작성', '비주얼 컨셉 개발', '광고 심의 대응', 'MV 연출'],
      mainNav: ['dashboard', 'inbox', 'campaign-hub'],
    },
    {
      key: 'AE',
      label: '영업 AE',
      labelEn: 'Account Executive',
      icon: 'ri-briefcase-line',
      description: '클라이언트를 유치하고 프로젝트를 관리합니다. 제안서 발표와 예산 협상을 담당합니다.',
      tasks: ['클라이언트 제안서 작성', '프레젠테이션 발표', '계약 협상', '프로젝트 PM'],
      mainNav: ['dashboard', 'inbox', 'campaign-hub', 'approval'],
    },
    {
      key: 'Production',
      label: '영상 PD',
      labelEn: 'Video Producer',
      icon: 'ri-video-line',
      description: 'MV와 광고 영상을 기획·제작합니다. 촬영 스케줄과 외주 스태프를 관리합니다.',
      tasks: ['촬영 스케줄 관리', '스태프 외주 계약', '편집 감독', '납품 데드라인 관리'],
      mainNav: ['dashboard', 'inbox', 'campaign-hub'],
    },
    {
      key: 'Data',
      label: '데이터 분석가',
      labelEn: 'Data Analyst',
      icon: 'ri-bar-chart-line',
      description: '캠페인 성과를 분석하고 최적화 방향을 제시합니다. CPM·CPC·전환율을 관리합니다.',
      tasks: ['캠페인 성과 리포트', '광고 최적화 분석', 'A/B 테스트', '클라이언트 대시보드'],
      mainNav: ['dashboard', 'inbox', 'marketing'],
    },
  ],
};

// ─── 기업 유형 메타 정보 ─────────────────────────────────────────
export type CompanyConfig = {
  key: CompanyType;
  label: string;
  labelEn: string;
  icon: string;
  description: string;
  features: string[];
  kpis: { label: string; unit: string }[];
  capital: string;
  isNonProfit?: boolean;
};

export const COMPANY_CONFIGS: Record<CompanyType, CompanyConfig> = {
  MusicLabel: {
    key: 'MusicLabel',
    label: '뮤직 레이블',
    labelEn: 'Music Label',
    icon: 'ri-music-2-line',
    description: '아티스트 발굴부터 앨범 제작, 음원 유통까지 케이팝 엔터테인먼트의 전 과정을 운영합니다.',
    features: ['A&R 크리에이티브 허브', '음원 정산 & 차트 추적', '아티스트 전속 계약 관리', '글로벌 유통 & IP 보호'],
    kpis: [
      { label: '음원 스트리밍 수익', unit: '만원/월' },
      { label: '아티스트 전속 계약 수', unit: '팀' },
      { label: '음반 판매량', unit: '장' },
    ],
    capital: '5,000만원',
  },
  LiveEntertainment: {
    key: 'LiveEntertainment',
    label: '공연기획사',
    labelEn: 'Live Entertainment',
    icon: 'ri-mic-line',
    description: '콘서트와 페스티벌을 기획·운영합니다. 아티스트 섭외부터 티켓 판매, 스폰서 유치까지.',
    features: ['공연 라인업 기획 & 섭외', '티켓 판매 전략 수립', '스폰서 계약 & 수익 관리', '무대 제작 예산 집행'],
    kpis: [
      { label: '객석 점유율', unit: '%' },
      { label: '티켓 판매 수익', unit: '만원' },
      { label: '스폰서 유치 금액', unit: '만원' },
    ],
    capital: '5,000만원',
  },
  CulturalFoundation: {
    key: 'CulturalFoundation',
    label: '문화재단',
    labelEn: 'Cultural Foundation',
    icon: 'ri-community-line',
    description: '정부 보조금과 기부금으로 문화예술 지원 사업을 운영하는 비영리 법인입니다.',
    features: ['보조금 신청 & 수주 관리', '문화사업 기획 & 집행', '후원자 CRM 시스템', '비영리 전표 & 회계'],
    kpis: [
      { label: '보조금 수주액', unit: '만원' },
      { label: '사업 수혜자 수', unit: '명' },
      { label: '사회공헌 지수', unit: '점' },
    ],
    capital: '정부 보조금 기반',
    isNonProfit: true,
  },
  WebtoonStudio: {
    key: 'WebtoonStudio',
    label: '웹툰 / IP 스튜디오',
    labelEn: 'Webtoon & IP Studio',
    icon: 'ri-quill-pen-line',
    description: '웹툰 원작 IP를 개발하고 드라마·게임·굿즈로 2차 판권 수익을 창출합니다.',
    features: ['연재 플랫폼 MOU 계약', 'IP 라이선싱 전략', '해외 판권 수출 협상', '저작권 등록 & 표절 방어'],
    kpis: [
      { label: '연재 작품 수', unit: '개' },
      { label: '플랫폼 MOU 수', unit: '개' },
      { label: '해외 판권 매출', unit: '만원' },
    ],
    capital: '5,000만원',
  },
  ContentAgency: {
    key: 'ContentAgency',
    label: '광고·콘텐츠 에이전시',
    labelEn: 'Content & Ad Agency',
    icon: 'ri-video-line',
    description: '브랜드 광고 캠페인과 뮤직비디오, SNS 콘텐츠를 기획·제작·운영합니다.',
    features: ['광고 캠페인 수주 & 운영', 'MV 제작 & 납품 관리', 'SNS 콘텐츠 전략', '성과 데이터 분석 & 최적화'],
    kpis: [
      { label: '수주 캠페인 수', unit: '건' },
      { label: '평균 전환율', unit: '%' },
      { label: '클라이언트 만족도', unit: '점' },
    ],
    capital: '5,000만원',
  },
};

// ─── 계정과목 (Chart of Accounts) ─────────────────────────────────
export type AccountCategory = '자산' | '부채' | '자본' | '수익' | '비용';

export type Account = {
  code: string;
  name: string;
  category: AccountCategory;
  companyTypes: CompanyType[] | 'all';
};

export const CHART_OF_ACCOUNTS: Account[] = [
  // 자산
  { code: '101', name: '현금', category: '자산', companyTypes: 'all' },
  { code: '102', name: '보통예금', category: '자산', companyTypes: 'all' },
  { code: '103', name: '외상매출금', category: '자산', companyTypes: 'all' },
  { code: '104', name: '선급금', category: '자산', companyTypes: 'all' },
  { code: '105', name: '미수수익', category: '자산', companyTypes: 'all' },
  { code: '106', name: '유형자산', category: '자산', companyTypes: 'all' },
  // 부채
  { code: '201', name: '외상매입금', category: '부채', companyTypes: 'all' },
  { code: '202', name: '선수금', category: '부채', companyTypes: 'all' },
  { code: '203', name: '미지급비용', category: '부채', companyTypes: 'all' },
  { code: '204', name: '미지급세금', category: '부채', companyTypes: 'all' },
  { code: '205', name: '단기차입금', category: '부채', companyTypes: 'all' },
  // 자본
  { code: '301', name: '자본금', category: '자본', companyTypes: 'all' },
  { code: '302', name: '이익잉여금', category: '자본', companyTypes: ['MusicLabel', 'LiveEntertainment', 'WebtoonStudio', 'ContentAgency'] },
  { code: '303', name: '기본재산', category: '자본', companyTypes: ['CulturalFoundation'] },
  // 수익 (공통)
  { code: '401', name: '매출액', category: '수익', companyTypes: ['MusicLabel', 'LiveEntertainment', 'WebtoonStudio', 'ContentAgency'] },
  { code: '402', name: '이자수익', category: '수익', companyTypes: 'all' },
  // 수익 (MusicLabel)
  { code: '411', name: '음원스트리밍수익', category: '수익', companyTypes: ['MusicLabel'] },
  { code: '412', name: '음반판매수익', category: '수익', companyTypes: ['MusicLabel'] },
  { code: '413', name: '공연출연료수익', category: '수익', companyTypes: ['MusicLabel'] },
  { code: '414', name: '광고협찬수익', category: '수익', companyTypes: ['MusicLabel'] },
  // 수익 (LiveEntertainment)
  { code: '421', name: '티켓판매수익', category: '수익', companyTypes: ['LiveEntertainment'] },
  { code: '422', name: '스폰서협찬수익', category: '수익', companyTypes: ['LiveEntertainment'] },
  { code: '423', name: '굿즈판매수익', category: '수익', companyTypes: ['LiveEntertainment'] },
  // 수익 (CulturalFoundation)
  { code: '431', name: '정부보조금수익', category: '수익', companyTypes: ['CulturalFoundation'] },
  { code: '432', name: '기업후원금수익', category: '수익', companyTypes: ['CulturalFoundation'] },
  { code: '433', name: '개인기부금수익', category: '수익', companyTypes: ['CulturalFoundation'] },
  // 수익 (WebtoonStudio)
  { code: '441', name: '연재플랫폼수익', category: '수익', companyTypes: ['WebtoonStudio'] },
  { code: '442', name: 'IP라이선싱수익', category: '수익', companyTypes: ['WebtoonStudio'] },
  { code: '443', name: '해외판권수익', category: '수익', companyTypes: ['WebtoonStudio'] },
  // 수익 (ContentAgency)
  { code: '451', name: '캠페인제작수익', category: '수익', companyTypes: ['ContentAgency'] },
  { code: '452', name: '영상제작수익', category: '수익', companyTypes: ['ContentAgency'] },
  { code: '453', name: 'SNS운영수익', category: '수익', companyTypes: ['ContentAgency'] },
  // 비용 (공통)
  { code: '501', name: '인건비', category: '비용', companyTypes: 'all' },
  { code: '502', name: '외주제작비', category: '비용', companyTypes: 'all' },
  { code: '503', name: '마케팅비', category: '비용', companyTypes: 'all' },
  { code: '504', name: '임대료', category: '비용', companyTypes: 'all' },
  { code: '505', name: '세금과공과', category: '비용', companyTypes: 'all' },
  { code: '506', name: '통신비', category: '비용', companyTypes: 'all' },
  { code: '507', name: '소모품비', category: '비용', companyTypes: 'all' },
  { code: '508', name: '감가상각비', category: '비용', companyTypes: 'all' },
  // 비용 (기업별 특수)
  { code: '511', name: '음반제작비', category: '비용', companyTypes: ['MusicLabel'] },
  { code: '512', name: '아티스트계약금', category: '비용', companyTypes: ['MusicLabel'] },
  { code: '521', name: '공연장대관료', category: '비용', companyTypes: ['LiveEntertainment'] },
  { code: '522', name: '무대장치비', category: '비용', companyTypes: ['LiveEntertainment'] },
  { code: '531', name: '사업비', category: '비용', companyTypes: ['CulturalFoundation'] },
  { code: '532', name: '후원행사비', category: '비용', companyTypes: ['CulturalFoundation'] },
  { code: '541', name: '원고료', category: '비용', companyTypes: ['WebtoonStudio'] },
  { code: '542', name: '플랫폼수수료', category: '비용', companyTypes: ['WebtoonStudio'] },
  { code: '551', name: '광고매체비', category: '비용', companyTypes: ['ContentAgency'] },
  { code: '552', name: '촬영장비렌탈', category: '비용', companyTypes: ['ContentAgency'] },
];

// ─── 전표 Mock 데이터 ──────────────────────────────────────────────
export type JournalLine = {
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  memo: string;
};

export type JournalEntry = {
  id: string;
  date: string;
  type: '입금전표' | '출금전표' | '대체전표';
  number: string;
  description: string;
  lines: JournalLine[];
  totalDebit: number;
  totalCredit: number;
  createdBy: string;
  status: '확정' | '임시저장';
};

export const MOCK_JOURNAL_ENTRIES: JournalEntry[] = [
  {
    id: 'je001',
    date: '2026.03.27',
    type: '입금전표',
    number: 'JE-2026-0327-001',
    description: '멜론 2월분 음원 수익 정산 수령',
    lines: [
      { accountCode: '102', accountName: '보통예금', debit: 3800, credit: 0, memo: '기업은행 입금' },
      { accountCode: '411', accountName: '음원스트리밍수익', debit: 0, credit: 3800, memo: '멜론 2월분' },
    ],
    totalDebit: 3800,
    totalCredit: 3800,
    createdBy: '재무 담당',
    status: '확정',
  },
  {
    id: 'je002',
    date: '2026.03.26',
    type: '출금전표',
    number: 'JE-2026-0326-001',
    description: '3월 전 직원 급여 지급',
    lines: [
      { accountCode: '501', accountName: '인건비', debit: 8500, credit: 0, memo: '3월 급여 총액' },
      { accountCode: '102', accountName: '보통예금', debit: 0, credit: 8500, memo: '급여 이체' },
    ],
    totalDebit: 8500,
    totalCredit: 8500,
    createdBy: '재무 담당',
    status: '확정',
  },
  {
    id: 'je003',
    date: '2026.03.25',
    type: '대체전표',
    number: 'JE-2026-0325-001',
    description: 'YoonArt 앨범커버 디자인 외주비 지급',
    lines: [
      { accountCode: '502', accountName: '외주제작비', debit: 2200, credit: 0, memo: '앨범커버 디자인' },
      { accountCode: '201', accountName: '외상매입금', debit: 0, credit: 2200, memo: 'YoonArt 미지급분' },
    ],
    totalDebit: 2200,
    totalCredit: 2200,
    createdBy: '재무 담당',
    status: '확정',
  },
  {
    id: 'je004',
    date: '2026.03.22',
    type: '입금전표',
    number: 'JE-2026-0322-001',
    description: 'K-POP 페스티벌 출연료 수령',
    lines: [
      { accountCode: '102', accountName: '보통예금', debit: 25000, credit: 0, memo: '행사출연료 입금' },
      { accountCode: '413', accountName: '공연출연료수익', debit: 0, credit: 25000, memo: 'K-POP 페스티벌' },
    ],
    totalDebit: 25000,
    totalCredit: 25000,
    createdBy: '재무 담당',
    status: '확정',
  },
  {
    id: 'je005',
    date: '2026.03.21',
    type: '출금전표',
    number: 'JE-2026-0321-001',
    description: '사무실 & 스튜디오 3월 임대료',
    lines: [
      { accountCode: '504', accountName: '임대료', debit: 4800, credit: 0, memo: '사무실+스튜디오 합산' },
      { accountCode: '102', accountName: '보통예금', debit: 0, credit: 4800, memo: '자동이체' },
    ],
    totalDebit: 4800,
    totalCredit: 4800,
    createdBy: '재무 담당',
    status: '확정',
  },
];
